<template>
  <div class="bg-warm-gray-10 flex justify-center">
    <div class="relative w-full flex flex-col space-y-8 min-w-5xl p-8">
      <div class="px-8 h-24 bg-white flex justify-end items-center fixed w-full left-0 top-0">
        <!-- <div class="flex flex-col items-start space-y-1">
          <img
            src="https://flex.countly.com/img/Flex-logo.b6fa644c.svg"
            class="w-32 h-8"
            alt="Countly Flex Logo"
          >
        </div> -->
        <div class="bg-white p-4 font-medium text-2xl z-[999] text-right">
          <div>{{ totalPrice }} {{ $t('mycountly.server-creation.per-month-plus-taxes') }}</div>
        </div>
      </div>

      <div class="flex flex-col space-y-4">
        <h2 class="font-bold">
          Plans
        </h2>
        <div class="grid grid-cols-4 gap-4">
          <div
            v-for="(plan, index) in plans"
            :key="index"
            :class="[plan._id === selectedPlan && 'border-2 border-[#1ea45c]', plan._id !== selectedPlan && 'border-2 border-cool-gray-20']"
            class="flex justify-between items-center bg-white rounded-[20px] p-4 cursor-pointer"
            @click="() => onPlanSelect(plan._id)"
          >
            <div class="flex flex-col">
              <div class="flex items-center gap-2">
                <div class="font-medium text-sm">
                  {{ plan.name }}
                </div>
                <span
                  v-if="plan.paddleTrialPeriod"
                  class="text-[10px] font-medium text-[#1ea45c] bg-[#1ea45c]/10 rounded px-1.5 py-0.5 whitespace-nowrap"
                >
                  {{ plan.paddleTrialPeriod.frequency }}-day trial available
                </span>
              </div>
              <div class="text-gray-500 font-light text-xs">
                {{ calculateMAU(plan.code) }} MAU&nbsp;·&nbsp;{{ dataPointsLabel(plan.code) }}
              </div>
              <div
                v-if="plan.basePricing.month.price.amount.$numberDecimal === '0'"
                class="mt-4 text-sm"
              >
                Free
              </div>
              <div
                v-else
                class="mt-4 text-sm"
              >
                {{ new Number(plan.basePricing.month.price.amount.$numberDecimal).toLocaleString("en-US", {style:"currency", currency:"USD"}) }}
                <span class="text-gray-500 font-light">{{ $t('mycountly.dashboard.deployments.per-month') }}</span>
              </div>
            </div>
            <div>
              <Check
                v-if="plan._id === selectedPlan"
                :stroke-width="2"
                class="w-5 h-5 text-[#1ea45c]"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col space-y-4">
        <h2 class="font-bold">
          Regions
        </h2>
        <div class="grid grid-cols-4 gap-4">
          <div
            v-for="(region, index) in regions"
            :key="index"
            :class="[!region.isAvailable && 'opacity-50 cursor-not-allowed', region._id === selectedRegion && 'border-2 border-[#1ea45c]', region._id !== selectedRegion && 'border-2 border-cool-gray-20']"
            class="flex justify-between items-center bg-white rounded-[20px] p-4 cursor-pointer"
            @click="() => onSelectRegion(region)"
          >
            <div>
              <div class="font-medium text-sm">
                {{ region.name }}
              </div>
              <div
                v-if="region.price === '0'"
                class="mt-4 text-sm"
              >
                Free
              </div>
              <div
                v-else-if="region.price"
                class="mt-4 text-sm"
              >
                {{ new Number(region.price).toLocaleString("en-US", {style:"currency", currency:"USD"}) }}
              </div>
              <div
                v-else
                class="text-sm mt-4 text-gray-500"
              >
                Choose a plan to see price
              </div>
            </div>
            <div>
              <Check
                v-if="region._id === selectedRegion"
                :stroke-width="2"
                class="w-5 h-5 text-[#1ea45c]"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col space-y-4">
        <h2 class="font-bold">
          Bundles
        </h2>
        <div class="grid grid-cols-2 gap-4">
          <div
            v-for="(bundle, index) in bundles"
            :key="index"
            :class="[bundle.price !== undefined && !bundle.isAvailable && 'opacity-50 cursor-not-allowed', selectedBundles.includes(bundle._id) && 'border-2 border-[#1ea45c]', !selectedBundles.includes(bundle._id) && 'border-2 border-cool-gray-20']"
            class="flex flex-col justify-between bg-white rounded-[20px] p-4 relative cursor-pointer"
            @click="() => onSelectBundle(bundle)"
          >
            <div>
              <div class="font-medium text-sm">
                {{ bundle.name }}
              </div>
              <ul class="mt-3 space-y-1.5">
                <li
                  v-for="feat in bundle.featureNames"
                  :key="feat"
                  class="flex items-start space-x-2 text-xs text-gray-600"
                >
                  <Check
                    :stroke-width="2"
                    class="w-3.5 h-3.5 text-[#1ea45c] shrink-0 mt-0.5"
                  />
                  <span>{{ feat }}</span>
                </li>
              </ul>
            </div>
            <div
              v-if="bundle.price === undefined"
              class="text-sm mt-4 text-gray-500"
            >
              Choose a plan to see price
            </div>
            <div
              v-else-if="!bundle.isAvailable"
              class="mt-4 text-sm"
            >
              Not available for this plan
            </div>
            <div
              v-else-if="bundle.price === '0'"
              class="mt-4 text-sm"
            >
              Free
            </div>
            <div
              v-else
              class="mt-4 text-sm"
            >
              {{ new Number(bundle.price).toLocaleString("en-US", {style:"currency", currency:"USD"}) }}
            </div>
            <Check
              v-if="selectedBundles.includes(bundle._id)"
              :stroke-width="2"
              class="absolute top-4 right-4 w-5 h-5 text-[#1ea45c]"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col space-y-4">
        <h2 class="font-bold">
          Domain Preference
        </h2>
        <div class="grid grid-cols-2 gap-4">
          <div
            v-for="(feature, index) in domains"
            :key="index"
            :class="[!feature.isAvailable && 'opacity-50 cursor-not-allowed', selectedDomain === feature._id && 'border-2 border-[#1ea45c]', selectedDomain !== feature._id && 'border-2 border-cool-gray-20']"
            class="flex justify-between items-between bg-white rounded-[20px] p-4 relative cursor-pointer"
            @click="() => onSelectDomain(feature)"
          >
            <div>
              <div class="font-medium text-sm">
                {{ feature.name.split('_')[0] }}
              </div>

              <div
                v-if="feature.price === '0'"
                class="mt-4 text-sm"
              >
                Free
              </div>
              <div
                v-else-if="feature.price"
                class="mt-4 text-sm"
              >
                {{ new Number(feature.price).toLocaleString("en-US", {style:"currency", currency:"USD"}) }}
              </div>
              <div
                v-else
                class="text-sm mt-4 text-gray-500"
              >
                Choose a plan to see price
              </div>
            </div>
            <div>
              <Check
                v-if="selectedDomain === feature._id"
                :stroke-width="2"
                class="absolute top-4 right-4 w-5 h-5 text-[#1ea45c]"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col space-y-4">
        <h2 class="font-bold">
          Data Retention
        </h2>
        <div class="grid grid-cols-4 gap-4">
          <div
            v-for="(feature, index) in retentions"
            :key="index"
            :class="[!feature.isAvailable && 'opacity-50 cursor-not-allowed', selectedRetention === feature._id && 'border-2 border-[#1ea45c]', selectedRetention !== feature._id && 'border-2 border-cool-gray-20']"
            class="flex justify-between items-between bg-white rounded-[20px] p-4 relative cursor-pointer"
            @click="() => onSelectRetention(feature)"
          >
            <div>
              <div class="font-medium text-sm">
                {{ feature.name }}
              </div>

              <div
                v-if="feature.price === '0'"
                class="mt-4 text-sm"
              >
                Free
              </div>
              <div
                v-else-if="feature.price"
                class="mt-4 text-sm"
              >
                {{ new Number(feature.price).toLocaleString("en-US", {style:"currency", currency:"USD"}) }}
              </div>
              <div
                v-else
                class="text-sm mt-4 text-gray-500"
              >
                Choose a plan to see price
              </div>
            </div>
            <div>
              <Check
                v-if="selectedRetention === feature._id"
                :stroke-width="2"
                class="absolute top-4 right-4 w-5 h-5 text-[#1ea45c]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Check from "@/components/icons/check.vue";
import * as DeploymentService from '@/modules/deployment/DeploymentService';
import { generalAppVariables } from '@/constants';
import { filterV3, featureCodeMatches } from '@/stores/server-creation';

export default {
  name: "PricingCalculator",
  components: {
    Check,
  },
  data() {
    return {
      plans: [],
      configurables: {
        gcp: {
          regions: [],
          sizes: [],
          features: [],
          providers: [],
        },
      },
      regions: [],
      features: [],
      bundles: [],
      domains: [],
      retentions: [],
      selectedPlan: null,
      selectedRegion: null,
      selectedBundles: [],
      selectedDomain: null,
      selectedRetention: null,
    };
  },
  computed: {
    totalPrice() {
      let total = 0;
      if (this.selectedPlan) {
        const plan = this.plans.find((p) => p._id === this.selectedPlan);
        total += parseFloat(plan.basePricing.month.price.amount.$numberDecimal || 0);
      }
      if (this.selectedRegion) {
        const region = this.regions.find((r) => r._id === this.selectedRegion);
        total += parseFloat(region.price || 0);
      }
      this.selectedBundles.forEach((bundleId) => {
        const bundle = this.bundles.find((b) => b._id === bundleId);
        total += parseFloat(bundle.price || 0);
      });
      if (this.selectedDomain) {
        const domain = this.domains.find((d) => d._id === this.selectedDomain);
        total += parseFloat(domain.price || 0);
      }
      if (this.selectedRetention) {
        const retention = this.retentions.find((r) => r._id === this.selectedRetention);
        total += parseFloat(retention.price || 0);
      }
      return total.toLocaleString("en-US", { style: "currency", currency: "USD" });
    },
  },
  watch: {
    selectedPlan(newPlan) {
      const plan = this.plans.find((p) => p._id === newPlan);

      this.regions.forEach((r, index) => {
        this.regions[index].price = plan.regions.find((feature) => feature.refId === r._id)?.pricing?.month?.price?.amount?.$numberDecimal || 0;
        this.regions[index].isAvailable = plan.regions.find((feature) => feature.refId === r._id)?.status === 'available' || false;
      });

      this.features.forEach((f, index) => {
        this.features[index].price = plan.features.find((pf) => pf.refId === f._id)?.pricing?.month?.price?.amount?.$numberDecimal || 0;
        this.features[index].isAvailable = plan.features.find((pf) => pf.refId === f._id)?.status === 'available' || false;
      });

      this.domains.forEach((d, index) => {
        this.domains[index].price = plan.features.find((pf) => pf.refId === d._id)?.pricing?.month?.price?.amount?.$numberDecimal || 0;
        this.domains[index].isAvailable = plan.features.find((pf) => pf.refId === d._id)?.status === 'available' || false;
      });

      this.retentions.forEach((r, index) => {
        this.retentions[index].price = plan.features.find((pf) => pf.refId === r._id)?.pricing?.month?.price?.amount?.$numberDecimal || 0;
        this.retentions[index].isAvailable = plan.features.find((pf) => pf.refId === r._id)?.status === 'available' || false;
      });

      this.bundles.forEach((b, index) => {
        const planBundle = (plan.bundles || []).find((pb) => pb.refId === b._id);
        // String '0' (not undefined) once a plan is chosen, so the card switches from
        // "Choose a plan to see price" to a real price / "Free" / "Not available".
        this.bundles[index].price = planBundle?.pricing?.month?.price?.amount?.$numberDecimal || '0';
        this.bundles[index].isAvailable = planBundle?.status === 'available' || false;
      });

      this.selectedRegion = null;
      this.selectedBundles = [];
      this.selectedDomain = null;
      this.selectedRetention = null;
      this.$forceUpdate(); // Force re-render to update prices and availability
    },
  },
  async mounted() {
    await this.loadConfigurables();
    // The catalog holds legacy duplicates (old codes) alongside the v3 entities. filterV3
    // keeps only the v3-coded ones so the pricing page never shows stale values. Feature
    // codes are version-prefixed ("v3_countly-base"), so exclusions use featureCodeMatches.
    const v3Features = filterV3(this.configurables.gcp.features);
    this.regions = filterV3(this.configurables.gcp.regions);
    this.features = v3Features.filter((feature) => feature.type === 'feature' && !featureCodeMatches(feature.code, 'countly-base') && !featureCodeMatches(feature.code, 'okta-ad'));
    this.domains = v3Features.filter((feature) => feature.type === 'domain');
    this.retentions = v3Features.filter((feature) => feature.type === 'retention').sort((a, b) => a.order - b.order);
    // Bundles replace individual feature add-ons: each bundle shows the features it
    // contains (resolved by id from the catalog); price/availability come from the plan.
    const featuresById = v3Features.reduce((acc, f) => {
      if (f._id) acc[String(f._id)] = f;
      return acc;
    }, {});
    this.bundles = filterV3(this.configurables.gcp.bundles || []).map((bundle) => ({
      ...bundle,
      featureNames: (bundle.features || [])
        .map((fid) => {
          const f = featuresById[String(fid)];
          return f ? (f.name || (f.meta && f.meta.catalogTitle)) : null;
        })
        .filter(Boolean),
    }));
  },
  methods: {
    onPlanSelect(planId) {
      this.selectedPlan = planId;
    },
    onSelectRegion(region) {
      if (region.isAvailable) {
        this.selectedRegion = region._id;
      }
    },
    onSelectBundle(bundle) {
      if (bundle.isAvailable) {
        const index = this.selectedBundles.indexOf(bundle._id);
        if (index > -1) {
          this.selectedBundles.splice(index, 1);
        } else {
          this.selectedBundles.push(bundle._id);
        }
      }
    },
    onSelectDomain(feature) {
      if (feature.isAvailable) {
        this.selectedDomain = feature._id;
      }
    },
    onSelectRetention(feature) {
      if (feature.isAvailable) {
        this.selectedRetention = feature._id;
      }
    },
    mauValue(planCode) {
      // Plan codes embed the MAU as a `mau_<n>_000` token, e.g.
      // v3_mau_5_000_gcp_base -> 5000, v3_mau_1000_000_gcp_base -> 1000000.
      const match = (planCode || '').toLowerCase().match(/mau_([\d_]+)/);
      return match ? parseInt(match[1].replaceAll('_', ''), 10) : 0;
    },
    calculateMAU(planCode) {
      return this.mauValue(planCode).toLocaleString();
    },
    dataPointsLabel(planCode) {
      // Allowed data points = MAU × 1000 (e.g. 5,000 MAU -> 5M data points).
      const dp = this.mauValue(planCode) * 1000;
      let abbr;
      if (dp >= 1e9) {
        abbr = `${(dp / 1e9).toFixed(dp % 1e9 === 0 ? 0 : 1)}B`;
      } else if (dp >= 1e6) {
        abbr = `${(dp / 1e6).toFixed(0)}M`;
      } else if (dp >= 1e3) {
        abbr = `${(dp / 1e3).toFixed(0)}K`;
      } else {
        abbr = String(dp);
      }
      return `${abbr} Data points`;
    },
    async getConfigurables(providerCode) {
      const configurablesRes = await DeploymentService.getConfigurablesPublic({ providerCode });

      if (configurablesRes.isOk()) {
        configurablesRes.value.payload.sizes = configurablesRes.value.payload.sizes.sort((a, b) => {
          if (a.size.value > b.size.value) {
            return 1;
          }
          if (a.size.value < b.size.value) {
            return -1;
          }
          return 0;
        });
        this.configurables[providerCode] = configurablesRes.value.payload;
      } else {
        this.$clyNotify({ message: 'Deployment configurables could not be loaded', sticky: false, type: 'error' });
      }
    },
    async getPlans() {
      const plansRes = await DeploymentService.getPlansPublic();

      if (plansRes.isOk()) {
        // v3 plan codes use the underscore format ("v3_mau_5_000_gcp_base"); match the
        // version prefix without the dot so the new pricing actually shows.
        this.plans = plansRes.value.payload.filter((plan) => plan.code.startsWith('v3') && plan.code.includes('gcp') && plan.status === "active" && !generalAppVariables.HIDDEN_PLAN_CODES.includes(plan.code)).sort((a, b) => a.priority - b.priority);
      } else {
        this.$clyNotify({ message: 'Plans could not be loaded', sticky: false, type: 'error' });
      }
    },
    async loadConfigurables() {
      await this.getConfigurables('gcp');
      await this.getPlans();
    },
  },
};
</script>
